import React from 'react';
import { Edit2, Loader2, Trash2, ChevronUp, ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PortfolioItem } from '../types';
import { SortableRow } from './SortableRow';
import { useTranslation } from 'react-i18next';
import { TooltipSpan } from './TooltipSpan';
import { formatCurrency, formatPerf, getExtendedMarketData } from '../lib/utils';
import { CompanyIcon } from './CompanyIcon';

export const PERF_OPTIONS = [
  { value: '1D', label: '1D' },
  { value: '1W', label: '1W' },
  { value: '1M', label: '1M' },
  { value: '3M', label: '3M' },
  { value: '6M', label: '6M' },
  { value: 'YTD', label: 'YTD' },
  { value: '1Y', label: '1Y' }
];

export function getPerfValue(item: any, liveQuotes: any, metric: string) {
  if (!item) return null;
  if (metric === '1D') {
    const liveVal = liveQuotes?.[item.ticker]?.perf1D;
    if (liveVal != null) return liveVal;
    if (item.analysis?.perf1D != null) return item.analysis.perf1D;
    if (typeof item.analysis?.regularMarketChangePercent === 'number') return item.analysis.regularMarketChangePercent / 100;
    return null;
  }
  if (metric === 'YTD') return item.analysis?.ytdReturn ?? null;
  return item.analysis?.[`perf${metric}`] ?? null;
}

export const MobileRowComponent = React.memo(({ item, isSelected, isTopPick, onUpdateTicker, onUpdateItem, onSetSelectedTicker, onRemoveItem, TickerEditorComponent, onMoveUp, onMoveDown, isFirst, isLast, liveQuotes, listPerfMetric, setListPerfMetric, showTechFundDetails, portfolioType, sortableId, tradeDuration = 'position' }: any) => {
  const { t } = useTranslation();
  const [showExtPrice, setShowExtPrice] = React.useState(false);
  const allocRef = React.useRef<HTMLInputElement>(null);
  
  const isHeldInRegular = portfolioType !== "WATCHLIST" && (parseFloat(String(item.allocation).replace(/[^0-9.-]+/g, "")) || 0) > 0;
  const isTaSell = item.analysis?.technical?.recommendation?.includes("SELL");
  const daysToNextEarnings = item.analysis?.technical?.daysToNextEarnings ?? item.analysis?.technical?.indicators?.daysToNextEarnings ?? (typeof (item.analysis as any)?.daysToNextEarnings === 'number' ? (item.analysis as any).daysToNextEarnings : null);
  const isEarningsRisk = Boolean(item.analysis?.technical?.isEarningsOverride || item.analysis?.technical?.indicators?.isEarningsOverride || (item.analysis as any)?.isEarningsOverride || (typeof daysToNextEarnings === 'number' && daysToNextEarnings !== null && daysToNextEarnings < 7));
  const isShortSwingTakeProfit = !isEarningsRisk && isHeldInRegular && tradeDuration === "short_swing" && (item.analysis?.confluence?.conviction <= 60 || item.analysis?.confluence?.recommendation === "NEUTRAL") && isTaSell;
  const confluenceRec = isEarningsRisk ? "NEUTRAL" : (isShortSwingTakeProfit ? "SELL" : (item.analysis?.confluence?.recommendation || "NEUTRAL"));

  const liveQ = liveQuotes?.[item.ticker];
  const extData = getExtendedMarketData(liveQ, item.analysis);
  const isLogoApplicable = item.analysis?.quoteType !== 'CURRENCY' && item.analysis?.quoteType !== 'INDEX' && item.analysis?.quoteType !== 'ETF' && item.analysis?.quoteType !== 'MUTUALFUND' && !item.ticker.includes('=X') && !item.ticker.startsWith('^');
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId || item.id! });

  const transformStr = CSS.Translate.toString(transform);
  const style = {
    transform: isDragging 
      ? `${transformStr || ''} scale(1.03)`.trim()
      : transformStr || undefined,
    transition,
    ...(isDragging ? { zIndex: 50, position: 'relative' as const } : {}),
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      onClick={() => {
        onSetSelectedTicker(item.ticker);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
      className={`p-0 border-b border-zinc-800/50 hover:bg-zinc-800/20 transition-colors relative cursor-pointer ${isSelected ? 'bg-zinc-800/40' : ''} ${isDragging ? 'bg-zinc-800 shadow-[0_20px_50px_rgba(0,0,0,0.5)] ring-2 ring-emerald-500 rounded-sm opacity-95 !transition-none z-50' : ''}`}
    >
      {isTopPick && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500 z-20 shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
      )}
      <div className="flex flex-col">
        {/* Row 1: Reorder [Left] | Symbol + Name [Center-Left] | Details + Delete [Right] */}
        <div className="flex justify-between items-start px-3 pt-3 pb-1">
          <div className="flex items-center gap-2 flex-1 min-w-0">
             {/* Drag Handle on the left */}
             <div 
               {...attributes} 
               {...listeners} 
               aria-label="Reorder row"
               className="flex items-center justify-center bg-zinc-800/40 p-1.5 rounded-sm cursor-move text-zinc-400 hover:text-emerald-400 touch-none w-8 min-h-[44px] shrink-0"
             >
                <GripVertical size={16} className="pointer-events-none" />
             </div>

             <div className="flex flex-col min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {isLogoApplicable ? (
                    <CompanyIcon ticker={item.ticker} domain={item.analysis?.website || item.analysis?.domain} size={20} className="rounded-sm shrink-0" />
                  ) : (
                    <div className="w-[20px] shrink-0" />
                  )}
                  <span className={`font-black tracking-tight text-base ${isSelected ? 'text-emerald-400' : 'text-zinc-200'}`}>
                    {item.ticker}
                  </span>
                  {isTopPick && (
                    <span className="badge-indigo px-1.5 py-0.5 shrink-0" title={t('analysis.pick_of_the_day')}>
                      🌟
                    </span>
                  )}
                  {item.analysis ? (
                    <span className={`text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-zinc-800/50 ${
                      confluenceRec.includes('BUY') ? 'text-emerald-500 border border-emerald-500/20' :
                      confluenceRec.includes('SELL') ? 'text-red-500 border border-red-500/20' :
                      'text-amber-500 border border-amber-500/20'
                    }`}>
                      {t(`common.status.${confluenceRec.toLowerCase() || 'neutral'}`, { defaultValue: confluenceRec })}
                    </span>
                  ) : (
                    <Loader2 size={10} className="animate-spin text-zinc-500" />
                  )}
                </div>
                {item.analysis?.name && (
                  <span className="text-[10px] text-zinc-400 font-bold mt-0.5 truncate max-w-[200px] sm:max-w-[280px] uppercase tracking-tighter" title={item.analysis.name}>
                    {item.analysis.name}
                  </span>
                )}
             </div>
          </div>

          <div className="flex items-center gap-1.5">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onSetSelectedTicker(item.ticker);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className={`btn-details h-7 w-7 p-0 flex items-center justify-center ${
                isSelected 
                  ? 'btn-details-selected' 
                  : 'btn-details-unselected'
              }`}
              title={t('common.details')}
            >
              <ChevronRight size={14} />
            </button>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onRemoveItem(item.id!, item.ticker);
              }}
              className="p-1 text-zinc-400 hover:text-red-400 hover:bg-red-400/10 rounded-sm transition-colors h-7 w-7 flex items-center justify-center"
              title={t('common.remove')}
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        {/* Row 2: Performance | Alloc | Price */}
        <div className={`flex justify-between items-center px-3 pt-1 bg-zinc-900/30 ${!showTechFundDetails ? 'pb-3' : 'pb-1'}`}>
          {portfolioType !== "WATCHLIST" && (
            <div className="flex items-center gap-1">
              <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest">{t('common.alloc')}</span>
              <div className="relative flex items-center">
                <input
                  ref={allocRef}
                  name="allocation"
                  aria-label="Portfolio allocation percentage"
                  type="text"
                  value={item.allocation}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => onUpdateItem(item.id!, 'allocation', e.target.value)}
                  className="input-underlined w-10 text-center font-mono text-[11px] text-zinc-300 bg-zinc-800/30 rounded-sm py-0.5"
                />
                <Edit2 size={8} className="ml-1 text-emerald-400 group-hover:text-emerald-300 transition-colors" />
              </div>
            </div>
          )}
          
          <div className="flex flex-col items-end gap-0.5 min-h-[28px]">
             {item.analysis && (
               <>
                <span 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowExtPrice(!showExtPrice);
                  }}
                  title={t('analysis.click_for_extended', { defaultValue: 'Click price to toggle extended hours' })}
                  className="text-[11px] font-mono font-bold text-zinc-300 cursor-pointer hover:underline decoration-dashed select-none whitespace-nowrap"
                >
                  {formatCurrency(item.analysis.price, item.analysis.currency)}
                </span>
                {showExtPrice && (
                  <span className="text-[8px] font-mono font-semibold text-amber-400 whitespace-nowrap">
                    {extData 
                      ? `${extData.shortLabel} ${formatCurrency(extData.price, item.analysis.currency)}`
                      : t("analysis.extended_unavailable", { defaultValue: "No EXT data" })}
                  </span>
                )}
                {getPerfValue(item, liveQuotes, listPerfMetric) != null && (
                  <span 
                    onClick={(e) => {
                       e.stopPropagation();
                       const idx = PERF_OPTIONS.findIndex(o => o.value === listPerfMetric);
                       setListPerfMetric(PERF_OPTIONS[(idx + 1) % PERF_OPTIONS.length].value);
                    }}
                    className={`text-[9px] font-bold cursor-pointer border-b border-dotted border-zinc-700 whitespace-nowrap ${getPerfValue(item, liveQuotes, listPerfMetric) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}
                  >
                    {formatPerf(getPerfValue(item, liveQuotes, listPerfMetric), listPerfMetric)}
                  </span>
                )}
               </>
             )}
          </div>
        </div>

        {/* Row 3: Tech/Fund Split Details */}
        {showTechFundDetails && (
          <div className="flex justify-between items-center px-3 pb-3 pt-1">
            <div className="flex flex-col gap-1 w-1/2">
              <span className="text-[9px] font-black text-zinc-400 uppercase tracking-widest">
                {t('common.technical')}
              </span>
              {item.analysis ? (
                <span className={`text-[13px] font-black ${
                  item.analysis.technical.recommendation.includes('BUY') ? 'text-emerald-500' :
                  item.analysis.technical.recommendation.includes('SELL') ? 'text-red-500' :
                  'text-amber-500'
                }`}>
                  {t(`common.status.${item.analysis.technical.recommendation.toLowerCase()}`, { defaultValue: item.analysis.technical.recommendation })}
                </span>
              ) : (
                <div className="flex items-center gap-1 text-zinc-300"><Loader2 size={10} className="animate-spin" /></div>
              )}
            </div>
            <div className="flex flex-col gap-1 w-1/2 items-end">
              <span className="text-[9px] font-black text-zinc-400 uppercase tracking-widest">
                {t('common.fundamental')}
              </span>
              {item.analysis ? (
                <span className={`text-[13px] font-black ${
                  item.analysis.fundamental.recommendation.includes('BUY') ? 'text-emerald-500' :
                  item.analysis.fundamental.recommendation.includes('SELL') ? 'text-red-500' :
                  'text-amber-500'
                }`}>
                  {t(`common.status.${item.analysis.fundamental.recommendation.toLowerCase()}`, { defaultValue: item.analysis.fundamental.recommendation })}
                </span>
              ) : (
                <div className="flex justify-end gap-1 text-zinc-300"><Loader2 size={10} className="animate-spin" /></div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export const DesktopRowComponent = React.memo(({ item, isSelected, isTopPick, onUpdateTicker, onUpdateItem, onSetSelectedTicker, onRemoveItem, TickerEditorComponent, liveQuotes, listPerfMetric, setListPerfMetric, showTechFundDetails, portfolioType, sortableId, tradeDuration = 'position' }: any) => {
  const { t } = useTranslation();
  const [showExtPrice, setShowExtPrice] = React.useState(false);
  const liveQ = liveQuotes?.[item.ticker];
  const extData = getExtendedMarketData(liveQ, item.analysis);
  const isLogoApplicable = item.analysis?.quoteType !== 'CURRENCY' && item.analysis?.quoteType !== 'INDEX' && item.analysis?.quoteType !== 'ETF' && item.analysis?.quoteType !== 'MUTUALFUND' && !item.ticker.includes('=X') && !item.ticker.startsWith('^');

  const isHeldInRegular = portfolioType !== "WATCHLIST" && (parseFloat(String(item.allocation).replace(/[^0-9.-]+/g, "")) || 0) > 0;
  const isTaSell = item.analysis?.technical?.recommendation?.includes("SELL");
  const daysToNextEarnings = item.analysis?.technical?.daysToNextEarnings ?? item.analysis?.technical?.indicators?.daysToNextEarnings ?? (typeof (item.analysis as any)?.daysToNextEarnings === 'number' ? (item.analysis as any).daysToNextEarnings : null);
  const isEarningsRisk = Boolean(item.analysis?.technical?.isEarningsOverride || item.analysis?.technical?.indicators?.isEarningsOverride || (item.analysis as any)?.isEarningsOverride || (typeof daysToNextEarnings === 'number' && daysToNextEarnings !== null && daysToNextEarnings < 7));
  const isShortSwingTakeProfit = !isEarningsRisk && isHeldInRegular && tradeDuration === "short_swing" && (item.analysis?.confluence?.conviction <= 60 || item.analysis?.confluence?.recommendation === "NEUTRAL") && isTaSell;
  const confluenceRec = isEarningsRisk ? "NEUTRAL" : (isShortSwingTakeProfit ? "SELL" : (item.analysis?.confluence?.recommendation || "NEUTRAL"));

  return (
    <SortableRow 
      id={sortableId || item.id!} 
      onClick={() => {
        onSetSelectedTicker(item.ticker);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
      className={`hover:bg-zinc-800/40 transition-colors group relative cursor-pointer ${isSelected ? 'bg-zinc-800/60' : ''}`}
    >
      <td className="px-4 py-4 relative">
        {isTopPick && (
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500 z-20 shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
        )}
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            {isLogoApplicable ? (
              <CompanyIcon ticker={item.ticker} domain={item.analysis?.website || item.analysis?.domain} size={20} className="rounded-sm" />
            ) : (
              <div className="w-[20px] shrink-0" />
            )}
            <span className={`font-black tracking-tight text-base ${isSelected ? 'text-emerald-400' : 'text-zinc-200 group-hover:text-white'}`}>
              {item.ticker}
            </span>
            {isTopPick && (
              <span className="badge-indigo px-1.5 py-0.5 shrink-0" title={t('analysis.pick_of_the_day')}>
                🌟
              </span>
            )}
          </div>
          <span className="text-[10px] text-zinc-400 px-1 mt-0.5 truncate max-w-[220px] block min-h-[16px]" title={item.analysis?.name}>
            {item.analysis?.name || " "}
          </span>
        </div>
      </td>
      <td className="px-4 py-4 text-right">
         {item.analysis ? (
            <div className="flex flex-col items-end gap-0.5">
              <span 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowExtPrice(!showExtPrice);
                }}
                title={t('analysis.click_for_extended', { defaultValue: 'Click price to toggle extended hours' })}
                className="text-mono-sm cursor-pointer hover:underline decoration-dashed select-none whitespace-nowrap"
              >
                {formatCurrency(item.analysis.price, item.analysis.currency)}
              </span>
              {showExtPrice && (
                <span className="text-[9px] font-mono font-semibold text-amber-400 whitespace-nowrap">
                  {extData 
                    ? `${extData.shortLabel} ${formatCurrency(extData.price, item.analysis.currency)}`
                    : t("analysis.extended_unavailable", { defaultValue: "No EXT data" })}
                </span>
              )}
              {getPerfValue(item, liveQuotes, listPerfMetric) != null && (
                <span 
                  onClick={(e) => {
                     e.stopPropagation();
                     const idx = PERF_OPTIONS.findIndex(o => o.value === listPerfMetric);
                     setListPerfMetric(PERF_OPTIONS[(idx + 1) % PERF_OPTIONS.length].value);
                  }}
                  className={`text-[10px] font-bold cursor-pointer hover:opacity-80 transition-opacity whitespace-nowrap ${getPerfValue(item, liveQuotes, listPerfMetric) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}
                >
                  {formatPerf(getPerfValue(item, liveQuotes, listPerfMetric), listPerfMetric)}
                </span>
              )}
            </div>
         ) : (
            <div className="flex justify-end text-zinc-300"><Loader2 size={12} className="animate-spin" /></div>
         )}
      </td>
      {portfolioType !== "WATCHLIST" && (
        <td className="px-2 py-4 text-right">
          <input
            name="allocation"
            aria-label="Portfolio allocation percentage"
            type="text"
            value={item.allocation}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onUpdateItem(item.id!, 'allocation', e.target.value)}
            className="input-underlined w-full text-right font-mono text-xs text-zinc-400"
          />
        </td>
      )}

      {/* Overall Recommendation (when collapsed) or Tech+Fund (when expanded) */}
      {!showTechFundDetails ? (
        /* Overall Recommendation Column */
        <td className="px-4 py-4 text-center text-[10px] font-bold whitespace-nowrap">
          {item.analysis ? (
            <span className={
              confluenceRec.includes('BUY') ? 'text-emerald-500 font-bold' :
              confluenceRec.includes('SELL') ? 'text-red-500 font-bold' :
              'text-amber-500'
            }>
              {t(`common.status.${confluenceRec.toLowerCase() || 'neutral'}`, { defaultValue: confluenceRec })}
            </span>
          ) : (
            <div className="flex justify-center text-zinc-300"><Loader2 size={12} className="animate-spin" /></div>
          )}
        </td>
      ) : (
        <>
          {/* Technical Column */}
          <td className="px-4 py-4 text-center text-[10px] font-bold whitespace-nowrap">
            {item.analysis ? (
              <span className={
                item.analysis.technical.recommendation.includes('BUY') ? 'text-emerald-500 font-bold' :
                item.analysis.technical.recommendation.includes('SELL') ? 'text-red-500 font-bold' :
                'text-amber-500'
              }>
                {t(`common.status.${item.analysis.technical.recommendation.toLowerCase()}`, { defaultValue: item.analysis.technical.recommendation })}
              </span>
            ) : (
              <div className="flex justify-center text-zinc-300"><Loader2 size={12} className="animate-spin" /></div>
            )}
          </td>
          {/* Fundamental Column */}
          <td className="px-4 py-4 pl-5 text-center text-[10px] font-bold whitespace-nowrap">
            {item.analysis ? (
              <span className={
                item.analysis.fundamental.recommendation.includes('BUY') ? 'text-emerald-500 font-bold' :
                item.analysis.fundamental.recommendation.includes('SELL') ? 'text-red-500 font-bold' :
                'text-amber-500'
              }>
                {t(`common.status.${item.analysis.fundamental.recommendation.toLowerCase()}`, { defaultValue: item.analysis.fundamental.recommendation })}
              </span>
            ) : (
              <div className="flex justify-center text-zinc-300"><Loader2 size={12} className="animate-spin" /></div>
            )}
          </td>
        </>
      )}
      <td className="px-4 py-4 pl-6 text-center">
        <div className="flex items-center justify-center gap-1.5">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onSetSelectedTicker(item.ticker);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`btn-details h-7 w-7 p-0 flex items-center justify-center ${
              isSelected 
                ? 'btn-details-selected' 
                : 'btn-details-unselected'
            }`}
            title={t('common.details')}
          >
            <ChevronRight size={14} />
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onRemoveItem(item.id!, item.ticker);
            }}
            className="p-1 text-zinc-300 hover:text-red-400 hover:bg-red-400/10 rounded-sm transition-colors"
            title={t('common.remove')}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </SortableRow>
  );
});

export const PerfRowComponent = React.memo(({ item, isTopPick, liveQuotes, onSetSelectedTicker }: any) => {
  const { t } = useTranslation();
  const perfRecord = liveQuotes[item.ticker];
  const perf1D = perfRecord?.perf1D ?? null;
  const isLogoApplicable = item.analysis?.quoteType !== 'CURRENCY' && item.analysis?.quoteType !== 'INDEX' && item.analysis?.quoteType !== 'ETF' && item.analysis?.quoteType !== 'MUTUALFUND' && !item.ticker.includes('=X') && !item.ticker.startsWith('^');
  const metrics = [
    { label: '1D', value: perf1D },
    { label: '1W', value: item.analysis?.perf1W },
    { label: '1M', value: item.analysis?.perf1M },
    { label: '3M', value: item.analysis?.perf3M },
    { label: '6M', value: item.analysis?.perf6M },
    { label: 'YTD', value: item.analysis?.ytdReturn },
    { label: '1Y', value: item.analysis?.perf1Y },
  ];

  return (
    <div 
      onClick={() => {
        onSetSelectedTicker(item.ticker);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
      className={`flex flex-col sm:flex-row sm:items-center hover:bg-zinc-800/40 transition-colors group border-b border-zinc-800/50 p-3 sm:p-0 relative cursor-pointer`}
    >
        {isTopPick && (
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500 z-20 shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
        )}
        
        <div className="flex-shrink-0 w-full sm:w-44 sm:px-4 sm:py-4 flex flex-row sm:flex-col items-center sm:items-start justify-between sm:justify-center mb-2 sm:mb-0 gap-2">
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2">
                {isLogoApplicable ? (
                  <CompanyIcon ticker={item.ticker} domain={item.analysis?.website || item.analysis?.domain} size={20} className="rounded-sm shrink-0" />
                ) : (
                  <div className="w-[20px] shrink-0" />
                )}
                <span className="text-ticker px-1 cursor-pointer hover:text-white" onClick={(e) => { e.stopPropagation(); onSetSelectedTicker(item.ticker); window.scrollTo({ top:0, behavior:'smooth'}); }}>{item.ticker}</span>
                {isTopPick && (
                  <span className="badge-indigo px-1.5 py-0.5 shrink-0" title={t('analysis.pick_of_the_day')}>
                    🌟
                  </span>
                )}
              </div>
              {item.analysis?.name ? (
                <span className="text-[10px] text-zinc-400 px-1 mt-0.5 truncate max-w-[200px] block min-h-[16px]" title={item.analysis.name}>{item.analysis.name}</span>
              ) : (
                <span className="text-[10px] text-zinc-400 px-1 mt-0.5 truncate max-w-[200px] block min-h-[16px]"> </span>
              )}
            </div>
            
            <div className="sm:hidden font-mono text-xs text-zinc-300 pr-1">
              {item.analysis ? formatCurrency(item.analysis.price, item.analysis.currency) : ''}
            </div>
        </div>

        <div className="flex-1 grid grid-cols-7 gap-0 pr-4 mt-2 sm:mt-0 items-center">
            {item.analysis ? (
               metrics.map((m, i) => (
                 <div key={m.label} className="col-span-1 flex flex-col items-end justify-center sm:px-2 py-1">
                    <span className="text-tiny-tracking sm:hidden mb-1">{m.label}(%)</span>
                    {m.value != null ? (
                      <span className={`text-[10px] sm:text-[11px] font-mono font-bold ${m.value > 0 ? 'text-emerald-500' : m.value < 0 ? 'text-red-500' : 'text-zinc-300'}`}>
                        {m.value >= 0 ? '+' : ''}{(m.value * 100).toFixed(2)}
                      </span>
                    ) : (
                      <span className="text-zinc-700 text-xs">-</span>
                    )}
                 </div>
               ))
            ) : (
               <div className="col-span-5 sm:flex-1 flex justify-center text-zinc-300"><Loader2 size={12} className="animate-spin" /></div>
            )}
        </div>
    </div>
  );
});

export function calculatePortfolioPerf(portfolio: any[], liveQuotes: any, portfolioType: string) {
  let hasAllocations = false;
  let totalAllocation = 0;

  const validItems = portfolio.filter((p: any) => p.analysis || liveQuotes?.[p.ticker]);
  if (validItems.length === 0) return null;

  const parseAlloc = (item: any) => {
    if (item.allocation == null) return 0;
    const raw = typeof item.allocation === 'string'
      ? parseFloat(item.allocation.replace(/[^0-9.-]+/g, ""))
      : parseFloat(item.allocation);
    return !isNaN(raw) && raw > 0 ? raw : 0;
  };

  validItems.forEach((item: any) => {
    const alloc = parseAlloc(item);
    if (alloc > 0) {
      hasAllocations = true;
      totalAllocation += alloc;
    }
  });

  const weights = validItems.map((item: any) => {
    const alloc = parseAlloc(item);
    if (portfolioType === "WATCHLIST") {
      return 1 / validItems.length;
    } else if (hasAllocations && totalAllocation > 0 && alloc > 0) {
      return alloc / totalAllocation;
    } else if (!hasAllocations) {
      return 1 / validItems.length;
    } else {
      return 0;
    }
  });

  const data: any = {
    perf1D: 0, perf1W: 0, perf1M: 0, perf3M: 0, perf6M: 0, ytdReturn: 0, perf1Y: 0
  };

  let count: any = { perf1D: 0, perf1W: 0, perf1M: 0, perf3M: 0, perf6M: 0, ytdReturn: 0, perf1Y: 0 };
  let sumW: any = { perf1D: 0, perf1W: 0, perf1M: 0, perf3M: 0, perf6M: 0, ytdReturn: 0, perf1Y: 0 };

  validItems.forEach((item: any, index: number) => {
    const w = weights[index];
    if (w === 0) return;

    const q = liveQuotes?.[item.ticker];
    const a = item.analysis;

    const map: any = {
      perf1D: q?.perf1D ?? a?.perf1D ?? (typeof a?.regularMarketChangePercent === 'number' ? a.regularMarketChangePercent / 100 : null),
      perf1W: q?.perf1W ?? a?.perf1W,
      perf1M: q?.perf1M ?? a?.perf1M,
      perf3M: q?.perf3M ?? a?.perf3M,
      perf6M: q?.perf6M ?? a?.perf6M,
      ytdReturn: q?.ytdReturn ?? a?.ytdReturn,
      perf1Y: q?.perf1Y ?? a?.perf1Y,
    };

    Object.keys(map).forEach(k => {
      if (map[k] != null && !isNaN(map[k])) {
        data[k] += map[k] * w;
        sumW[k] += w;
        count[k]++;
      }
    });
  });

  Object.keys(data).forEach(k => {
    if (count[k] > 0 && sumW[k] > 0) {
      data[k] = data[k] / sumW[k];
    } else {
      data[k] = null;
    }
  });

  return data;
}

export const PortfolioPerfRowComponent = React.memo(({ portfolio, liveQuotes, portfolioType }: any) => {
  const { t } = useTranslation();

  const perfData = React.useMemo(() => {
    return calculatePortfolioPerf(portfolio, liveQuotes, portfolioType);
  }, [portfolio, liveQuotes, portfolioType]);

  if (!perfData) return null;

  const metrics = [
    { label: '1D', value: perfData.perf1D },
    { label: '1W', value: perfData.perf1W },
    { label: '1M', value: perfData.perf1M },
    { label: '3M', value: perfData.perf3M },
    { label: '6M', value: perfData.perf6M },
    { label: 'YTD', value: perfData.ytdReturn },
    { label: '1Y', value: perfData.perf1Y },
  ];

  return (
    <div className={`flex flex-col sm:flex-row sm:items-center bg-zinc-800/80 hover:bg-zinc-800 transition-colors group border-y border-zinc-700/50 p-3 sm:p-0 relative mt-2 sm:mt-0 shadow-lg shadow-black/20`}>
      <div className="flex-shrink-0 w-full sm:w-44 sm:px-4 sm:py-4 flex flex-row sm:flex-col items-center sm:items-start justify-between sm:justify-center mb-2 sm:mb-0 gap-2">
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-ticker text-sm px-1 text-zinc-100">
              {t("common.portfolio")}
            </span>
          </div>
          <span className="text-[10px] text-zinc-400 px-1 mt-0.5 truncate max-w-[200px]" title={t("common.weighted_performance")}>
            {t("common.weighted_performance")}
          </span>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-7 gap-0 pr-4 mt-2 sm:mt-0 items-center">
        {metrics.map((m, i) => (
          <div key={m.label} className="col-span-1 flex flex-col items-end justify-center sm:px-2 py-1">
            <span className="text-tiny-tracking sm:hidden mb-1">{m.label}(%)</span>
            {m.value != null ? (
              <span className={`text-[10px] sm:text-[11px] font-mono font-bold ${m.value > 0 ? 'text-emerald-500' : m.value < 0 ? 'text-red-500' : 'text-zinc-300'}`}>
                {m.value >= 0 ? '+' : ''}{(m.value * 100).toFixed(2)}
              </span>
            ) : (
              <span className="text-zinc-700 text-xs">-</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});